/**
 * Drop-directory persistence using IndexedDB.
 *
 * The accountant picks a directory once via showDirectoryPicker(); we
 * stash the resulting FileSystemDirectoryHandle in IndexedDB so it
 * survives reloads. Chrome remembers the persisted permission across
 * sessions; on revoke we re-prompt.
 *
 * The dashboard's pull loop can then write decrypted files into this
 * directory without bothering the user again.
 */

const DB_NAME = "dovach-dashboard";
const DB_VERSION = 1;
const STORE = "kv";
const KEY_DROP_DIR = "drop_dir_handle";

declare global {
  // FS Access API types - minimal shims for projects whose lib.dom doesn't
  // ship them yet.
  interface Window {
    showDirectoryPicker(options?: { mode?: "read" | "readwrite" }): Promise<
      FileSystemDirectoryHandle
    >;
  }
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function kvGet<T>(key: string): Promise<T | undefined> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve(req.result as T | undefined);
    req.onerror = () => reject(req.error);
  });
}

async function kvPut<T>(key: string, value: T): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function kvDelete(key: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.objectStore(STORE).delete(key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Returns the persisted directory handle, or null if none is saved. */
export async function loadDropDirHandle(): Promise<FileSystemDirectoryHandle | null> {
  try {
    const handle = await kvGet<FileSystemDirectoryHandle>(KEY_DROP_DIR);
    return handle ?? null;
  } catch {
    return null;
  }
}

export async function saveDropDirHandle(handle: FileSystemDirectoryHandle): Promise<void> {
  await kvPut(KEY_DROP_DIR, handle);
}

export async function clearDropDirHandle(): Promise<void> {
  await kvDelete(KEY_DROP_DIR);
}

/**
 * Prompt the user for a directory if none is saved. Re-prompts if the
 * permission was revoked. Returns the handle (with confirmed write
 * permission) or throws if the user declined.
 */
export async function pickDropDirectory(): Promise<FileSystemDirectoryHandle> {
  if (typeof window.showDirectoryPicker !== "function") {
    throw new Error("FS_ACCESS_UNSUPPORTED");
  }
  const handle = await window.showDirectoryPicker({ mode: "readwrite" });
  await saveDropDirHandle(handle);
  return handle;
}

/**
 * Try to use the saved handle. Returns null if the handle isn't saved
 * OR the permission was revoked. Caller falls back to pickDropDirectory.
 */
export async function ensureDropDirAccess(): Promise<FileSystemDirectoryHandle | null> {
  const handle = await loadDropDirHandle();
  if (!handle) return null;
  try {
    // queryPermission returns "granted" if Chrome still trusts our claim;
    // if the user revoked or this is a different machine we get "prompt".
    const status = await (handle as unknown as {
      queryPermission(opts: { mode: "readwrite" }): Promise<"granted" | "prompt" | "denied">;
    }).queryPermission({ mode: "readwrite" });
    if (status === "granted") return handle;
    // Try to re-acquire silently (Chrome 121+ allows this on persisted handles).
    const requested = await (handle as unknown as {
      requestPermission(opts: { mode: "readwrite" }): Promise<"granted" | "denied" | "prompt">;
    }).requestPermission({ mode: "readwrite" });
    return requested === "granted" ? handle : null;
  } catch {
    return null;
  }
}

// The nine characters NTFS and the Chrome File System Access API both
// reject in filenames or directory names: < > : " / \ | ? *. Without
// stripping these, a doc label like `mv"m` would cause getFileHandle
// to throw "Name is not allowed" forever and the matrix cell would
// stay yellow -> instead of green check.
//
// Hebrew gershayim U+05F4 is a separate, legal character and stays.
// Only the ASCII straight quote U+0022 is stripped, which is what
// most keyboards actually produce for Hebrew abbreviations like m"m.
const RESERVED_FS_NAME_CHARS = /[<>:"/\\|?*]/g;

/**
 * Replace characters that Windows + the Chrome File System Access API
 * reject in filenames or directory names with `_`. Trailing dots and
 * spaces are also stripped (Windows refuses them on path components).
 */
export function sanitizeFsName(name: string): string {
  const sanitized = name.replace(RESERVED_FS_NAME_CHARS, "_");
  return sanitized.replace(/[. ]+$/, "").trim();
}

/**
 * Write `bytes` to `subpath/filename` inside the drop directory. Creates
 * intermediate directories as needed. Overwrite is allowed - the route
 * resolver upstream is responsible for filename uniqueness.
 *
 * Path parts and the filename itself are passed through `sanitizeFsName`
 * before they hit the FSA - the server is allowed to compute
 * resolved_filename from arbitrary doc labels, and the local FS won't
 * accept the same set of characters Oracle does.
 */
export async function writeFileToDropDir(
  rootHandle: FileSystemDirectoryHandle,
  subpath: string,
  filename: string,
  bytes: Uint8Array,
): Promise<void> {
  let dir = rootHandle;
  const parts = subpath.split("/").map(sanitizeFsName).filter(Boolean);
  for (const part of parts) {
    dir = await dir.getDirectoryHandle(part, { create: true });
  }
  const safeName = sanitizeFsName(filename) || "untitled.bin";
  const file = await dir.getFileHandle(safeName, { create: true });
  const writable = await file.createWritable();
  // Slice into a fresh ArrayBuffer view so writable.write doesn't choke
  // on a Uint8Array that aliases a SharedArrayBuffer (Safari quirk).
  await writable.write(bytes.slice().buffer);
  await writable.close();
}
